//@flow
import { Map } from 'immutable';
import memoize from 'memoizee';
import _ from 'underscore';
import FunctionStore from '../Base/functionStoreClass';
import { updateState, setFlag, setFlagProp, getFlagProp, loadDataObject } from '../Base/storeUtils';
import QuoteModel from './quoteModel';
import QuoteTotalsModel from './quoteTotalsModel';
import QuoteItemsCollection from './quoteItemsCollection';
import APIInterface from '../../APIInterfaces/quotes';
import Constants from '../../constants';
import UrlData from '../../Utils/url-data';
import CreditCardsStore from '../CreditCards/creditCards';
import CookiesData from '../../Utils/cookies-data';
import CategoriesStore from '../MemberCategories/categories';
import MembershipStore from '../Membership/membershipStore';
import GiftUtils from '../../Utils/gifts';
import SessionStore from '../Session/session';
import NumberUtils from '../../Utils/number';
import { toImmutable, toJS } from '../../Utils/immutable';

import type { HandlerAdd, StoreState } from '../Base/functionStoreClass';
import type ProductsCollection from '../Products/productsCollection';
import type APIResponseReader from '../../Utils/api-response-reader';
import type { QuoteObject } from './quoteModel';

export type Options = { quoteItems:QuoteItemsCollection, products:ProductsCollection };

// see defaultInitialData as an example of the structure of the data.
const defaultInitialData = {
  quotes: {
    /*
      quoteId: {
        data: {
          id: 1234,
          is_guest: true,
          payment: {
            processor_type: 'stripe',
            vault_id: 8910,
          },
          shipping_address_id: 825595,
          shipping_rate_id: 'checkout_checkout',
          coupon_code: 'test_code',
        },

        flags: {
          isAddCreditCardSuccess: false,
          attemptedSetPromoCode: false,
          itemReserveFailed: false,
        },

        flagsOnUpdate: {
          isAddCreditCardSuccess: false,
        }
      }
    */
  },
  flags: { },
  _temp: { },
};

const _createQuoteTotalsModel = memoize((data:any, quoteItems:QuoteItemsCollection, products:ProductsCollection) => {
  return new QuoteTotalsModel(toJS(data), quoteItems, products);
});

export default class QuoteStoreClass extends FunctionStore {
  constructor(initialDataObj:Object = defaultInitialData) {
    super(initialDataObj);
  }

  // This is a hack for now since the sync code has the potential to spawn a
  // ton of quote creation requests.  In the future, we should either create
  // the quote on demand or do this somewhere like PageWrapper
  _createQuoteInProgress:boolean = false;

  /*
    returns QuoteModel;

    it will return a quote model with the information of the quote, if the
    store hasn't synced with the backend yet it will use the API to get the
    quote.
  */
  getQuote():QuoteModel {
    return this._findQuoteModel(data => !data.ss_order_date && !data.ss_locked);
  }

  getQuoteById(id:string):QuoteModel {
    return this._findQuoteModel(data => data.id === id);
  }

  getQuoteTotals(quoteId:?string, options:Options):QuoteTotalsModel {
    return loadDataObject(
      this.getState(),
      ['quotes', NumberUtils.toString(quoteId), 'data', 'totals'],
      (data:any) => _createQuoteTotalsModel(data, options.quoteItems, options.products));
  }

  /*
    Returns a quote items collection (a new instance everytime),
    will make use of the API if no data is found and return an empty collection
  */
  getQuoteItems(quoteId:?string):QuoteItemsCollection {
    return this._resolvePropertyObject(
      ['quotes', NumberUtils.toString(quoteId), 'data', 'items'],
      QuoteItemsCollection);
  }

  getAddQuoteItemErrorSku():string|boolean {
    return this.getState().get('addQuoteItemErrorSku', false);
  }

  _syncData() {
    const guestQuoteId = CookiesData.getGuestQuoteId();
    const isLoggedIn = this._isLoggedIn();

    if (!isLoggedIn && guestQuoteId) {
      return APIInterface.getQuote(guestQuoteId);
    }

    // In this state we are trying to log in the user, wait before creating a quote.
    if (!isLoggedIn && this._getFlag('waitForLogin')) return;

    // In this state the quote is being assigned to the fresh logged in user, wait before making any request.
    // see responses to LOGIN and LOGGED_IN
    if (isLoggedIn && guestQuoteId) return;

    if (isLoggedIn) return APIInterface.getQuotes();

    // Create a new quote for a guest user.
    _createCartQuote(this);
  }

  getSubActiveQuote():QuoteModel {
    return this._findQuoteModel(data => data.ss_order_date && !data.ss_locked);
  }

  getSubProcessingQuote():QuoteModel {
    return this._findQuoteModel(data => data.ss_locked);
  }

  isAddShippingSuccess():boolean {
    return this._getFlag('isAddShippingSuccess');
  }

  isAddCreditCardSuccess(quoteId:?string):boolean {
    return _getQuoteFlag(this, quoteId, 'isAddCreditCardSuccess');
  }

  isItemReserveFailed(quoteId:?string):boolean {
    return _getQuoteFlag(this, quoteId, 'itemReserveFailed');
  }

  _setup(onSuccess:HandlerAdd, onError:HandlerAdd) {
    /*
      sets loggedIn flag to true.
    */
    onSuccess(Constants.LOGGED_IN, updateState(_respondToLogin), [SessionStore]);

    onSuccess(Constants.LOGIN_WITH_MAGIC_LINK, setFlag('waitForLogin', true));

    /*
      Once the user logs in, we need to make a PUT to the quotes endpoint
      to tie the quote to the user so that they GET the correct quote. Also
      need to remove the quote ID cookie once they login so that it doesn't cause
      errors if they leave checkout flow.
      sets loggedIn flag to true.
    */
    onSuccess(Constants.LOGIN, updateState(_respondToLogin), [SessionStore]);
    onSuccess(Constants.CREATE_CUSTOMER_SUCCESS, updateState(_respondToLogin), [SessionStore]);

    /*
      resets the store data to its initial default data
    */
    onSuccess(Constants.LOGOUT, _resetData);

    /*
      - replace and saves the reponse body on the quotes data
      - sets the 'synced' flag to true.
      - Makes the request to create a quote if there are no quotes, if
        the request succeed it will trigger CREATE_QUOTE_SUCCESS.
        if we have information about the default shipping address id, the
        quote will be created with that information or we will make PUT
        request to update the quote if it doesn't have set the shipping id.
    */
    onSuccess(Constants.GET_QUOTES_SUCCESS, [_respondToGetQuotesSuccess, _addItemAndRemove]);
    onSuccess(Constants.GET_QUOTE_SUCCESS, [_respondToGetQuoteSuccess, _addItemAndRemove, _checkShouldSubscribe, _checkShouldSetCustomerGroup]);

    /*
      - adds the quote coming in the response to the data, to the
        beggining of the quotes array.
        if the new quote doesn't have a shipping_address_id set and we now
        have information about the default shipping address id, it will make
        a PUT request to update the quote
    */
    onSuccess(Constants.CREATE_QUOTE_SUCCESS, [_respondToCreateQuoteSuccess, _addItemAndRemove, _checkShouldSubscribe, _checkShouldSetCustomerGroup]);

    onSuccess(Constants.UPDATE_QUOTE, _respondToUpdateQuote);

    /*
      - Sets any flags that were previously specified to be set on update.
      - Updates the quote data with the new data form the response body.
      - Sets the "mustReserve" flag to false if the items were reserved by the request.
      - Makes sure the quote uses the default values for empty attributes.
    */
    onSuccess(Constants.UPDATE_QUOTE_SUCCESS, _respondToUpdateQuoteSuccess);

    onSuccess(Constants.RESET_RESERVATION_FAILURE, _resetReservationFailure);

    /*
      used to set `cardsLoaded` flag so we know that the credit cards have loaded
    */
    onSuccess(Constants.GET_CREDIT_CARDS_SUCCESS, _respondToGetCreditCardsSuccess, [CreditCardsStore]);

    /*
      used to get the `vault_id` from the membership
    */
    onSuccess(Constants.GET_MEMBERSHIP_SUCCESS, _respondToGetMembershipSuccess, [MembershipStore]);
    onSuccess(Constants.GET_CUSTOMER_SUCCESS, _respondToGetMembershipSuccess, [MembershipStore]);

    /*
      - Sends a request to update the shipping address id on the quote.
      - Specifies that we should set the "isAddShippingSuccess" flag to true after the next
        successful quote update.
    */
    onSuccess(Constants.ADD_SHIPPING_ADDRESS_CHECKOUT, _respondToAddShippingAddressCheckout);

    /*
      - Sends a request to set the billing address id and the payment info on the quote.
      - Specifies that we should set the "isAddCreditCardSuccess" flag to true after the next
        successful quote update.
    */
    onSuccess(Constants.ADD_CREDIT_CARD_SUCCESS, _respondToAddCreditCard);

    /*
      It will save the information of the default shipping address id,
      if we already have a quote and it doesn't have a shipping id set it
      will make a PUT request to set it.
    */
    onSuccess(Constants.GET_CUSTOMER_SUCCESS, _respondToGetCustomerSuccess);
    onSuccess(Constants.UPDATE_CUSTOMER_DETAILS_SUCCESS, _respondToGetCustomerSuccess);

    /*
      Uses the API to set the payment info on the quote (PUT),
      it will trigger UPDATE_QUOTE_SUCCESS on success.
    */
    onSuccess(Constants.UPDATE_QUOTE_PAYMENT_INFO, _respondToUpdatePaymentInfo);

    /*
      Sends a request to update the shipping address id on the quote.
    */
    onSuccess(Constants.SET_QUOTE_SHIPPING_ADDRESS, _respondToSetShippingAddress);

    onSuccess(Constants.SET_PROMO_CODE_SUCCESS, _respondToGetQuoteSuccess);

    /*
      Uses the API to add an item to the quote or adds it directly the quote
      is fake.
    */
    onSuccess(Constants.ADD_QUOTE_ITEM, updateState(_addQuoteItemResponse));

    /*
      Uses the API to add quote items
    */
    onSuccess(Constants.ADD_QUOTE_ITEMS, _addQuoteItemsResponse);

    /*
      Uses the API to set the qty of the item, or sets the qty
      directly on the fake quote items.
    */
    onSuccess(Constants.UPDATE_QUOTE_ITEM_QTY, _setQuoteItemQtyResponse);

    /*
      Uses the API to delete an item or deletes the fake items directly
    */
    onSuccess(Constants.DELETE_QUOTE_ITEM, _deleteQuoteItemResponse);

    onSuccess(Constants.ADD_QUOTE_ITEM_SUCCESS, _respondToAddQuoteItemSuccess);
    /*
      - Sets the _temp.urlPromoCode variable, which is used to set the coupon_code in
        `updateQuotesWithDefaultValues`
      - Sends a request to update the coupon_code on the quote if the quote currently doesn't
        have a coupon code, or if it has the default coupon code.
    */
    onSuccess(Constants.SET_URL_PROMO_CODE, _respondToSetUrlPromoCode);

    /*
      set mustReserveFlag.
    */
    onSuccess(Constants.SET_MUST_RESERVE_QUOTE, _setMustReserveQuote);

    /*
      Responds to successful order by resetting data of quote store but leaving flags and temp data.
    */
    onSuccess(Constants.CREATE_ORDER_SUCCESS, updateState(_respondToLogin), [SessionStore]);

    onSuccess(Constants.RESET_CHECKOUT_STEP_STATE, _respondToResetCheckoutStepState);

    onSuccess(Constants.UPDATE_MEMBERSHIP_PRICING, _respondToUpdateMembershipPricing);

    onSuccess(Constants.SET_MSRP_CUSTOMER_GROUP, _setMsrpCustomerGroup);

    onSuccess(Constants.RESERVE_QUOTE_SUCCESS, _respondToReserveQuote);

    onSuccess(Constants.SET_NEW_MEMBER_PLAN, _setNewMemberPlan);

    /*
      If the request was to "/quotes" and the response has an error with "Invalid coupon code",
      sets the "isPromoCodeError" flag to true.
    */
    onError(Constants.UPDATE_QUOTE_SUCCESS, [_resetCookieAndQuoteFlag, _handleInvalidMemPlan]);
    onError(Constants.GET_QUOTE_SUCCESS, _removeCookieError);

    onError(Constants.LOGIN, setFlag('waitForLogin', false));
    /*
      Sets the store value addQuoteItemErrorSku to a sku number like 1244 or true
    */
    onError(Constants.ADD_QUOTE_ITEM_SUCCESS, updateState(_addQuoteItemErrorResponse));

    onError(Constants.NOT_AUTHORIZED, _resetData);
  }

  _isSynced():boolean {
    return this._getFlag('synced');
  }

  _isLoggedIn():boolean {
    return this._getFlag('loggedIn');
  }

  _findQuoteModel(predicate:(quote:Object) => boolean):QuoteModel {
    if (!this._isSynced()) this._syncData();

    const quotes = toJS(this.getState().get('quotes', []));
    const sortedQuotes = _.sortBy(quotes, quote => (quote && quote.data && -1 * quote.data.id) || 0);
    const quote = _.compact(sortedQuotes).find(quote => quote && quote.data && predicate(quote.data));

    const quoteId = quote && quote.data && quote.data.id;
    return this._resolvePropertyObject(['quotes', NumberUtils.toString(quoteId), 'data'], QuoteModel);
  }
}

// --- Action responses--
function _checkShouldSubscribe(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  const shouldSubscribe = UrlData.getShouldSubscribe();
  if (!shouldSubscribe) return;

  const quoteId = store.getQuote().getId();
  if (!quoteId) return;

  APIInterface.updateQuote(quoteId, { subscribe: true });

  // To avoid future extraneous calls to update
  // subscription from url params, clear
  // `should_subscribe` param now
  UrlData.removeParamAndUpdateHistory('should_subscribe');
}

function _checkShouldSetCustomerGroup(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  const customerGroup = UrlData.getCustomerGroup();
  if (!customerGroup) return;

  const quoteId = store.getQuote().getId();
  if (!quoteId) return;

  APIInterface.updateQuote(quoteId, { customer_group: customerGroup });

  // To avoid future extraneous calls to update
  // customer group from url params, clear
  // `customer_group` param now
  UrlData.removeParamAndUpdateHistory('customer_group');
}

function _addQuoteItemResponse(state:StoreState, { quoteId, sku, qty, params }) {
  if (!quoteId) return;
  if (state.get('addQuoteItemErrorSku', false)) state.set('addQuoteItemErrorSku', false);
  APIInterface.addItem(quoteId, sku, qty || 1, params || {});
}

function _addQuoteItemsResponse(store:FunctionStore, { quoteId, quoteItems }) {
  if (!quoteId) return;
  APIInterface.addItems(quoteId, quoteItems);
}

function _setQuoteItemQtyResponse(store:FunctionStore, { itemId, qty, quoteId }) {
  APIInterface.updateItemQty(quoteId, itemId, qty);
}

function _deleteQuoteItemResponse(store:FunctionStore, { quoteId, itemId }) {
  APIInterface.deleteItems(quoteId, [ itemId ]);
}

function _addQuoteItemErrorResponse(state:StoreState, apiResponse:APIResponseReader) {
  const errorMessage = apiResponse.getErrors()[0];
  const skuMatch = /sku:([^\s]+)/;
  const errorMatch = skuMatch.exec(errorMessage);
  state.set('addQuoteItemErrorSku', (errorMatch && errorMatch[1]) || true);
}

function _addItemAndRemove(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  let items = UrlData.getQuoteItems();
  if (items) {
    const quoteId = store.getQuote().getId();
    if (!quoteId) return;

    items = items.split(',').map((item) => ({sku: item, qty: 1}));
    APIInterface.addItems(quoteId, items);

    UrlData.removeQuoteItems();
  }

  const reorder = UrlData.getReorderToken();
  if (reorder) {
    const quoteId = store.getQuote().getId();
    if (!quoteId) return;

    APIInterface.addItemsByReorderToken(quoteId, reorder);

    UrlData.removeReorderToken();
  }
}

function _setMustReserveQuote(store:QuoteStoreClass) {
  if (store.getQuote().isReserved()) return;
  store._updateState(state => setFlagProp(state, 'mustReserve', true));
  _updateQuotesWithDefaultValues(store);
}

function _respondToUpdateMembershipPricing(store:FunctionStore, data:{ quoteId:string, membershipPricing:boolean }) {
  //The backend will convert 'New' to 'membership' customers with existing orders
  const customerGroup = data.membershipPricing ? 'Membership' : 'MSRP';
  APIInterface.updateQuote(data.quoteId, { customer_group: customerGroup });
}

function _respondToAddQuoteItemSuccess(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  const categories = CategoriesStore.getCategories();
  const giftsCategory = categories.findByUrlKey('m-gifts');
  const giftSkus = giftsCategory && giftsCategory.getProductsSkus();
  const itemData = apiResponse.getResponseBody().items;
  //TODO refactor or remove this logic...
  _.each(itemData, item => {
    if (giftSkus && giftSkus.find(sku => sku === item.sku) || (item.sku === GiftUtils.EGIFT_SKU)) {
      _setGiftFlags(store);
    }
  });
}

//Reset the reservation error so the OOS modal doens't reappear
function _resetReservationFailure(store:QuoteStoreClass) {
  const quoteId = store.getQuote().getId();
  return quoteId && _setQuoteFlag(store, quoteId, 'itemReserveFailed', false);
}

//sets MSRP customer group for gift items and Egifts, since we want MSRP toggle enabled for those.
//Also makes sure that we don't hvae subscribe and save set for any gifts/egifts.
function _setGiftFlags(store:QuoteStoreClass) {
  const quoteId = store.getQuote().getId();
  quoteId && _setQuoteFlag(store, quoteId, 'resetSubscribeToSave', true);
  _setMsrpCustomerGroup(store);
}

function _setMsrpCustomerGroup(store:QuoteStoreClass) {
  const quoteId = store.getQuote().getId();
  quoteId && _setQuoteFlag(store, quoteId, 'setMsrp', true);
  _updateQuotesWithDefaultValues(store);
}

function _respondToResetCheckoutStepState(store:FunctionStore) {
  store._updateRawData((data) => {
    _.each(data.quotes, (quote, quoteId) => {
      quote.flags = quote.flags || {};
      quote.flags['isAddCreditCardSuccess'] = false;
    });
  });
  store._updateState(state => setFlagProp(state, 'isAddShippingSuccess', false));
}

function _respondToGetQuotesSuccess(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  store._updateRawData((data:Object) => {
    const quotesData = apiResponse.getResponseBody();
    const quotesIds = _.map(quotesData, quote => quote['id']);

    data.quotes = data.quotes || {};

    //Remove any extra quote.
    Object.keys(data.quotes).forEach((quoteId) => {
      if (quotesIds.includes(quoteId)) return;
      delete data.quotes[quoteId];
    });

    // Set quotes data.
    _.each(quotesData, (quoteData, id) => {
      data.quotes[id] = data.quotes[id] || {};
      data.quotes[id].data = quoteData;
    });

    data.flags.synced = true;
  });

  return _handleGuestQuote(store);
}

function _respondToGetQuoteSuccess(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  if (store._isLoggedIn() && apiResponse.getResponseBody().is_guest) {
    store._setFlag('guestQuoteHasItems', !_.isEmpty(apiResponse.getResponseBody().items));
    return _handleGuestQuote(store);
  }

  store._updateState(state => {
    _updateQuoteData(state, apiResponse.getResponseBody());
    // if the user is not logged in this is enought to be synced.
    if (!getFlagProp(state, 'loggedIn')) setFlagProp(state, 'synced', true);
  });

  if (store._isSynced() && !store.getQuote().getId()) _createCartQuote(store);
  // in case the new quote dosen't have set a shipping_address_id and we have
  // now the default id.
  _updateQuotesWithDefaultValues(store);
}

function _handleGuestQuote(store:QuoteStoreClass) {
  if (!store._isSynced()) return;

  const activeQuoteId = store.getQuote().getId();
  const activeQuoteHasItems = activeQuoteId ? store.getQuoteItems(activeQuoteId).models.length : false;
  const guestQuoteId = CookiesData.getGuestQuoteId();
  const guestQuoteHasItems = store._getFlag('guestQuoteHasItems');
  const mustReserve = store._getFlag('mustReserve');

  if (guestQuoteId && (guestQuoteHasItems || !activeQuoteId || !activeQuoteHasItems)) {
    return APIInterface.updateQuote(guestQuoteId, { is_guest: false, reserve_items: mustReserve });
  }

  if (!activeQuoteId && !guestQuoteId) _createCartQuote(store);

  // in case the new quote dosen't have set a shipping_address_id and we have
  // now the default id.
  _updateQuotesWithDefaultValues(store);
}

function _respondToCreateQuoteSuccess(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  store._updateState(state => _updateQuoteData(state, apiResponse.getResponseBody()));

  // in case the new quote dosen't have set a shipping_address_id and we have
  // now the default id.
  _updateQuotesWithDefaultValues(store);

  if (!store._isLoggedIn()) {
    // Save new quote as guest quote if is valid.
    const currentQuote = store.getQuote();
    const currentId = currentQuote.getId();
    if (currentId && currentQuote.isGuest()) {
      CookiesData.setGuestQuoteId(currentId);
      store._updateState(state => setFlagProp(state, 'synced', true));
    }
  }
}

function _respondToUpdateQuote(store:FunctionStore, { quoteId, quoteData }) {
  APIInterface.updateQuote(quoteId, quoteData);
}

function _respondToUpdateQuoteSuccess(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  const newQuoteData = apiResponse.getResponseBody();
  const quoteId = newQuoteData.id;
  _setFlags(store, quoteId);

  //remove the stored cookie if the quote has been set to a member
  const isGuest = apiResponse.getResponseBody()['is_guest'];
  const storedQuoteId = CookiesData.getGuestQuoteId();
  if (isGuest === false && storedQuoteId) CookiesData.remove('guestQuote');

  store._updateState(state => {
    //if we just set an amazon payment method, then the CC's vaultId should be removed
    if (newQuoteData.payment && newQuoteData.payment.processor_type === 'amazon') state.setIn([ '_temp', 'vaultId' ], null);
    state.setIn([ 'quotes', NumberUtils.toString(quoteId), 'data' ], toImmutable(newQuoteData));
    if (newQuoteData.reserve_expiration) setFlagProp(state, 'mustReserve', false);
    setFlagProp(state, 'updatingQuote', false);
  });

  //if payment method is amazon, we are using promises, don't need to update Quote w. default values
  if (newQuoteData.payment && newQuoteData.payment.processor_type === 'amazon') return;
  // make sure the quote uses the latest default values for empty attibutes.
  _updateQuotesWithDefaultValues(store);
}

function _respondToAddShippingAddressCheckout(store:FunctionStore, data:{addressId:string, quoteId:string}) {
  /*
   * Set default shipping address id. This will be used to update the id in case the initial
   * request is dropped.
   */
  store._updateState(state => {
    state.setIn([ '_temp', 'defaultShippingAddressId' ], data.addressId);
    setFlagProp(state, 'isAddShippingSuccess', true);
  });
  APIInterface.updateQuote(data.quoteId, { shipping_address_id: data.addressId });
}

function _respondToAddCreditCard(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  const vaultId = apiResponse.getResponseBody()['id'];
  const method = apiResponse.getResponseBody()['processor_type'];

  const data = apiResponse.getRequestData();
  const quoteId = data['quoteId'];
  _setQuoteFlag(store, quoteId, 'isAddCreditCardSuccess', true, 'flagsOnUpdate');

  store._updateState(state => {
    state.setIn([ '_temp', 'vaultId' ], vaultId);
    state.setIn(['_temp', 'paymentMethod'], method);
  });

  _updateQuotesWithDefaultValues(store);
}

function _respondToReserveQuote(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  const newQuoteData = apiResponse.getResponseBody();
  const quoteId = newQuoteData.id;

  if (newQuoteData && newQuoteData.items) {
    if (!_itemsReserved(newQuoteData.items)) {
      _setQuoteFlag(store, quoteId, 'itemReserveFailed', true);
    } else {
      _setQuoteFlag(store, quoteId, 'itemReserveFailed', false);
    }
  }
}

function _itemsReserved(items:Object) {
  return _.every(items, item => item.is_reserved == true);
}

function _respondToGetCustomerSuccess(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  const addresses = apiResponse.getResponseBody().addresses;
  const defaultShippingAddressId = _getDefaultShippingAddressId(addresses);

  if (!defaultShippingAddressId) return;
  store._updateState(state => state.setIn([ '_temp', 'defaultShippingAddressId' ], defaultShippingAddressId));
  _updateQuotesWithDefaultValues(store);
}

function _respondToUpdatePaymentInfo(store:FunctionStore, data:Object) {
  APIInterface.updateQuote(data.quoteId, {
    payment: {
      processor_type: 'stripe',
      vault_id: data.vaultId,
    },
  });
}

function _respondToSetShippingAddress(store:FunctionStore, { quoteId, addressId }) {
  APIInterface.updateQuote(quoteId, { shipping_address_id: addressId });
}

/*For MSRP customers, we need to set the default payment method on the quote in response
to get credit card success because MSRP customers dont' have a membership to store payment method */
function _respondToGetCreditCardsSuccess(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  store._setFlag('cardsLoaded', true);
  const cardData = apiResponse.getResponseBody();
  store._updateRawData((newData) => {
    const quotes = newData.quotes;
    _.each(quotes, quote => {
      if (quote && quote.data && (quote.data.customer_group === 'MSRP')){
        //try to set amazon payment first, if it has a billing_agreement_id it's amazon
        let payment = _.find(cardData, (card) => !!card.billing_agreement_id);
        //if there is no amazon payment, use a random credit card as default payment
        if (!payment) payment = _.sample(cardData);
        newData._temp.vaultId = (payment && (payment.billing_agreement_id || payment.id));
        newData._temp.paymentMethod = payment && payment.processor_type;
      }
    });
  });
  _updateQuotesWithDefaultValues(store);
}

function _respondToGetMembershipSuccess(store:QuoteStoreClass) {
  const membership = MembershipStore.getMembership();
  const vaultId = membership.getPaymentVaultId();
  const billingAgreementId = membership.getAmazonBillingId();

  if (billingAgreementId) {
    store._updateState(state => state.setIn([ '_temp', 'billingAgreementId' ], billingAgreementId));
    return _updateQuotesWithDefaultValues(store);
  }

  if (vaultId) {
    // store membership data
    store._updateState(state => state.setIn([ '_temp', 'vaultId' ], vaultId));
    _updateQuotesWithDefaultValues(store);
  }
}

function _respondToSetUrlPromoCode(store:QuoteStoreClass, { code }:{ code:string }) {
  store._updateState(state => state.setIn([ '_temp', 'urlPromoCode' ], code));
  _updateQuotesWithDefaultValues(store);
}

function _resetCookieAndQuoteFlag(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  store._setFlag('updatingQuote', false);
  _removeCookieError(store, apiResponse);
}

function _removeCookieError(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  // only remove the cookie if there is an actual error
  if (!apiResponse.getErrors().length) return;
  CookiesData.remove('guestQuote');
}

function _handleInvalidMemPlan(store:QuoteStoreClass, apiResponse:APIResponseReader) {
  // If we are trying to set a wrong membership plan remove it from the _temp object to not try it again
  if (!apiResponse.getErrors().includes('Invalid membership plan')) return;
  store._updateState(state => state.removeIn(['_temp', 'newMembershipPlan']));
}

function _respondToLogin(state:StoreState) {
  const guestQuoteId = CookiesData.getGuestQuoteId();
  const guestQuoteItems = state.getIn(['quotes', guestQuoteId, 'data', 'items']);
  const loggedIn = SessionStore.isLoggedIn();
  //TODO: Now that we are reading from the session store this flag could be removed.
  setFlagProp(state, 'loggedIn', loggedIn);
  setFlagProp(state, 'synced', false);
  setFlagProp(state, 'guestQuoteHasItems', !!(guestQuoteItems && guestQuoteItems.size));
  state.set('quotes', Map());
  if (!guestQuoteId || !loggedIn) return;
  //check for reservation so it doesn't get removed by the update success resultant of login
  APIInterface.getQuotes();
}

function _setNewMemberPlan(store:QuoteStoreClass, { code }:{code:string}) {
  store._updateState(state => state.setIn([ '_temp', 'newMembershipPlan' ], code));
  _updateQuotesWithDefaultValues(store);
}

// --- Action responses ---



// --- Setters ---
function _setFlags(store:FunctionStore, quoteId:string) {
  store._updateState((state) => {
    const id = NumberUtils.toString(quoteId);
    const flagsToSet = state.getIn([ 'quotes', id, 'flagsOnUpdate' ]);
    if (!flagsToSet) return;

    flagsToSet.forEach((value:boolean, key:string) => {
      state.setIn([ 'quotes', id, 'flags', key ], value);
    });

    state.deleteIn([ 'quotes', id, 'flagsOnUpdate' ]);
  });
}

function _resetData(store:QuoteStoreClass) {
  store._createQuoteInProgress = false;
  store._reset(defaultInitialData);
}

function _setQuoteFlag(store:FunctionStore, quoteId:string, flag:string, value:boolean, type:string = 'flags') {
  store._updateState((state:StoreState) => {
    state.setIn([ 'quotes', NumberUtils.toString(quoteId), type, flag ], value);
  });
}

// --- Setters ---



// --- Helpers ---
function _createCartQuote(store:QuoteStoreClass) {
  if (store._createQuoteInProgress) return;

  store._createQuoteInProgress = true;
  APIInterface.createQuote();
}

function _updateQuoteData(state:StoreState, data:Object) {
  state.setIn([ 'quotes', NumberUtils.toString(data.id), 'data' ], toImmutable(data));
}

function _getQuoteFlag(store:FunctionStore, quoteId:?string, flag:string):boolean {
  return store.getState().getIn([ 'quotes', NumberUtils.toString(quoteId), 'flags', flag ], false);
}

function _getDefaultShippingAddressId(addresses) {
  if (!addresses) return;
  let defaultId;
  const defaultShippingAddress = _.find(addresses, (address) => {
    defaultId = address.id;
    return address['is_default_shipping'] === true;
  });

  return defaultShippingAddress ? defaultShippingAddress['id'] :
    defaultId;
}

function _updateQuotesWithDefaultValues(store:QuoteStoreClass) {
  const state = store.getState();
  //Don't try to update if there is a current update in progress
  if (getFlagProp(state, 'updatingQuote')) return;
  const defaultShippingAddressId = state.getIn([ '_temp', 'defaultShippingAddressId' ]);
  const mustReserve = getFlagProp(state, 'mustReserve');
  const paymentInfo = _getQuotePaymentInfo(state);
  const defaultVaultId = state.getIn([ '_temp', 'vaultId' ]);
  const defaultPaymentMethod = state.getIn(['_temp', 'paymentMethod']);
  const urlPromoCode = state.getIn([ '_temp', 'urlPromoCode' ]);
  const isLoggedIn = getFlagProp(state, 'loggedIn');
  const billingAgreementId = state.getIn([ '_temp', 'billingAgreementId' ]);
  const newMembershipPlan = state.getIn(['_temp', 'newMembershipPlan']);

  const data = store.getRawData();
  const quotes = data.quotes;
  Object.keys(quotes).forEach((quoteId) => {
    const quote:QuoteObject = data.quotes[quoteId].data;
    if (!quote || !quoteId) return;
    let updates:Object = {};
    const isFree = quote.payment && quote.payment.method === 'free';
    const isNewQuote = !quote.payment || !quote.payment.hasOwnProperty('card_id');
    const hasCardId = !!quote.payment && !!quote.payment.card_id;
    const isGuestQuote = quote.is_guest;
    //only set amazon if there are items in the cart, currently magento rejects amazonMethod w.out items in cart
    const isAddingAmazon = mustReserve && billingAgreementId && quote.payment && (quote.payment.card_id !== billingAgreementId);
    const isVirtual = quote.is_virtual;
    const setMsrp = _getQuoteFlag(store, quoteId, 'setMsrp');
    const resetSubscribeToSave = _getQuoteFlag(store, quoteId, 'resetSubscribeToSave');
    const isValidDefaultPayment = defaultPaymentMethod &&
      quote.available_payment_methods &&
      quote.available_payment_methods.includes(defaultPaymentMethod) &&
      defaultVaultId;

    //update quote for returning members
    if (!isFree && paymentInfo && isValidDefaultPayment) {
      if (isNewQuote || !hasCardId) {
        updates = Object.assign({}, paymentInfo);
      }
    }

    if (isAddingAmazon) {
      updates['payment'] = {
        processor_type: 'amazon',
        billing_agreement_id: billingAgreementId,
      };
    }

    // only reserve the cart quote.
    if (mustReserve && quoteId === NumberUtils.toString(store.getQuote().getId())) {
      updates['reserve_items'] = true;
    }

    if (!isVirtual && defaultShippingAddressId && !quote.shipping_address_id) {
      updates['shipping_address_id'] = defaultShippingAddressId;
    }

    if (isLoggedIn && isGuestQuote) {
      // API will complain if updates is empty
      updates['is_guest'] = false;
    }

    if (!_getQuoteFlag(store, quoteId, 'attemptedSetPromoCode')) {

      /*
       * If we have a promo code provided by the url params, we will use it to set the coupon_code
       * on the quote if the quote has no code or if it has the default code, but we will not
       * overwrite any other code that's already on the quote.
       * If there is no promo code from the url and there's no code already on the quote, then we
       * apply the default code.
       */
      if (urlPromoCode && (!quote['coupon_code'] || _isDefaultPromoCode(quote['coupon_code']))) {
        _setQuoteFlag(store, quoteId, 'attemptedSetPromoCode', true);
        updates['coupon_code'] = urlPromoCode;
      }
    }

    if (setMsrp && (quote.customer_group === 'New' || quote.customer_group === 'General')) {
      _setQuoteFlag(store, quoteId, 'setMsrp', false);
      updates['customer_group'] = 'MSRP';
    }

    if (resetSubscribeToSave && quote.subscribe === true) {
      _setQuoteFlag(store, quoteId, 'resetSubscribeToSave', false);
      updates['subscribe'] = false;
    }

    if (newMembershipPlan && newMembershipPlan !== quote.new_membership_plan_code) {
      updates['new_membership_plan_code'] = newMembershipPlan;
    }

    if (Object.keys(updates).length > 0) {
      store._updateState(state => setFlagProp(state, 'updatingQuote', true));
      APIInterface.updateQuote(quoteId, updates);
    }
  });
}

// return payment info data for quote
// * requires membership and credit cards data to be loaded already
function _getQuotePaymentInfo(state:StoreState) {
  const vaultId = state.getIn([ '_temp', 'vaultId' ]);
  const method = state.getIn(['_temp', 'paymentMethod']);
  if (!vaultId) return null;

  return {
    payment: {
      method,
      vault_id: vaultId,
    },
  };
}

function _isDefaultPromoCode(code:?{name:?string}) {
  return code && code.name;
}
